import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Typography, Pagination, Grid, Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import PropTypes from 'prop-types';

/**
 * DataTable Component - Reusable table with server-side pagination
 * Optimized from USER's TablePagination template
 */
export default function DataTable({
  columns,
  apiCallBack,
  queryKey,
  propsInput = {},
  isRefetch = 0,
  whereCondition = {},
  onRowClick, // Callback khi click vào hàng
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  // State for pagination - Init from URL or default
  const [pagination, setPagination] = useState({
    showSizeChanger: true,
    pageSize: Number(searchParams.get('limit')) || 10,
    current: Number(searchParams.get('page')) || 1,
    total: 0,
    pageSizeOptions: ['10', '20', '50', '100'],
  });

  const [sort, setSort] = useState({
    sortBy: searchParams.get('sortBy') || null,
    sortOrder: searchParams.get('sortOrder') || null,
  });

  // Sync state when URL params change (e.g. browser back button)
  useEffect(() => {
    const page = Number(searchParams.get('page')) || 1;
    const limit = Number(searchParams.get('limit')) || 10;
    const sortBy = searchParams.get('sortBy');
    const sortOrder = searchParams.get('sortOrder');

    setPagination((prev) => {
      if (prev.current !== page || prev.pageSize !== limit) {
        return { ...prev, current: page, pageSize: limit };
      }
      return prev;
    });

    setSort((prev) => {
      if (prev.sortBy !== sortBy || prev.sortOrder !== sortOrder) {
        return { sortBy, sortOrder };
      }
      return prev;
    });
  }, [searchParams]);

  // React Query to fetch data
  const { data, refetch, isLoading, isFetching, isError, error } = useQuery({
    queryKey: [queryKey, pagination.current, pagination.pageSize, whereCondition, sort],
    queryFn: async () => {
      // Build Params
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...whereCondition,
      };

      if (sort.sortBy && sort.sortOrder) {
        params.sortBy = sort.sortBy;
        params.sortOrder = sort.sortOrder;
      }

      const response = await apiCallBack(params);
      return response;
    },
    placeholderData: (previousData) => previousData, // Keep data while fetching new page
  });

  // Refetch when isRefetch trigger changes
  useEffect(() => {
    if (isRefetch > 0) {
      refetch();
    }
  }, [isRefetch, refetch]);

  // Process data for Table
  const { dataSource, totalItems } = useMemo(() => {
    if (!data) return { dataSource: [], totalItems: 0 };

    let list = [];

    // Check if data itself is an array (Direct array response)
    if (Array.isArray(data)) {
      list = data;
    } else {
      // 1. Try to find the list in common root properties
      const candidates = [
        data.users,
        data.reviews,
        data.roles,
        data.configs,
        data.data,
        data.items, // Common standard
        data.list, // Added for Drama
      ];

      // Find the first valid array candidate
      const foundArray = candidates.find((c) => Array.isArray(c));

      if (foundArray) {
        list = foundArray;
      } else if (data.data && typeof data.data === 'object') {
        // 2. If no array found at root, check if 'data' is an object that CONTAINS the array (Nested pagination structure)
        // Example: { success: true, data: { roles: [...], total: 10 } }
        const nestedData = data.data;
        list =
          nestedData.users ||
          nestedData.reviews ||
          nestedData.roles ||
          nestedData.configs ||
          nestedData.data ||
          nestedData.items ||
          nestedData.list ||
          [];
      } else {
        list = [];
      }
    }

    // Final safety check
    if (!Array.isArray(list)) {
      list = [];
    }

    const total =
      data.totalItem || data.totalItems || data.total || data.totalPages || data.pagination?.totalItems || data.pagination?.total || data.data?.total || list.length || 0;

    const formatted = list.map((item, index) => {
      const newItem = {
        ...item,
        stt: (pagination.current - 1) * pagination.pageSize + (index + 1),
        // Create a safe alias for 'key' since it is a reserved property in Antd/React
        originalDataKey: item.key,
      };

      // Ensure we have a unique row key (React Key)
      if (newItem.key === undefined || newItem.key === null) {
        newItem.key = item.id !== undefined ? item.id : index;
      }
      return newItem;
    });

    return { dataSource: formatted, totalItems: total };
  }, [data, pagination.current, pagination.pageSize]);

  // Handle Table change (filters, sorter)
  const handleTableChange = (newPagination, filters, sorter) => {
    const sortField = Array.isArray(sorter) ? sorter[0] : sorter;
    const params = new URLSearchParams(searchParams);

    if (sortField?.order) {
      const sortBy = sortField.field;
      const sortOrder = sortField.order === 'ascend' ? 'ASC' : 'DESC';

      setSort({ sortBy, sortOrder });
      params.set('sortBy', sortBy);
      params.set('sortOrder', sortOrder);
    } else {
      setSort({ sortBy: null, sortOrder: null });
      params.delete('sortBy');
      params.delete('sortOrder');
    }
    setSearchParams(params);
  };

  const onPaginationChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize,
    }));
    // Update URL params
    const params = new URLSearchParams(searchParams);
    params.set('page', page);
    params.set('limit', pageSize);
    setSearchParams(params);
  };

  // Responsive: Check for mobile
  const screens = Grid.useBreakpoint();

  // Automatically add STT column if not present and handle responsive fixed columns
  const tableColumns = useMemo(() => {
    // If mobile (md is false), we disable fixed columns to prevent layout issues
    // Note: screens is empty on initial render, so we default to true (desktop) to avoid flash, or false.
    // Better to default false for mobile-first or check if screens.md is strictly undefined.
    const isMobile = screens.md === false;

    const sttColumn = {
      title: 'STT',
      dataIndex: 'stt',
      key: 'stt',
      width: 60,
      align: 'center',
      fixed: isMobile ? undefined : 'left', // Only fix on desktop
      render: (text) => <span className="text-gray-400 font-medium">{text}</span>,
    };

    let finalColumns = columns;

    // Check if STT is already defined to avoid duplication
    if (!columns.some((col) => col.key === 'stt' || col.dataIndex === 'stt')) {
      finalColumns = [sttColumn, ...columns];
    }

    // Responsive override: Remove 'fixed' property from all columns if on mobile
    if (isMobile) {
      return finalColumns.map((col) => ({ ...col, fixed: undefined }));
    }
    return finalColumns;
  }, [columns, screens.md]);

  // Handle errors
  if (isError) {
    return (
      <div className="p-8 text-center bg-red-50 rounded-xl m-4 border border-red-100">
        <Typography.Text type="danger" className="block mb-3 font-medium">
          Đã có lỗi xảy ra khi tải dữ liệu
        </Typography.Text>
        <Typography.Text className="block mb-4 text-xs text-gray-500">{error?.message}</Typography.Text>
        <Button onClick={() => refetch()} type="primary" danger ghost>
          Thử lại
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Table
        {...propsInput}
        bordered
        columns={tableColumns}
        dataSource={dataSource}
        loading={isLoading || isFetching}
        onChange={handleTableChange}
        pagination={false} // Disable internal pagination
        scroll={{ x: 'max-content', ...propsInput.scroll }}
        onRow={
          onRowClick
            ? (record) => ({
                onClick: (e) => {
                  // Bỏ qua click vào các nút action (button, link, icon, dropdown, ...)
                  if (e.target.closest('button, a, .ant-btn, .ant-dropdown-trigger, .ant-popconfirm, .ant-popover, .ant-switch, .ant-table-selection-column, .ant-checkbox-wrapper, .ant-checkbox, input')) {
                    return;
                  }
                  onRowClick(record);
                },
                style: { cursor: 'pointer' },
              })
            : undefined
        }
        rowClassName={(record, index) => {
          const baseClass = index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30';
          const hoverClass = onRowClick ? 'hover:bg-blue-50 transition-colors' : '';
          if (typeof propsInput.rowClassName === 'function') {
            return `${propsInput.rowClassName(record, index)} ${hoverClass}`;
          }
          if (typeof propsInput.rowClassName === 'string') {
            return `${propsInput.rowClassName} ${hoverClass}`;
          }
          return `${baseClass} ${hoverClass}`;
        }}
      />

      {/* Custom Pagination Footer */}
      {totalItems > 0 && (
        <div className="py-4 px-4 border-t border-gray-100 bg-white rounded-b-xl">
          {screens.xs ? (
            // Mobile Layout
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between w-full">
                <Select
                  value={pagination.pageSize}
                  onChange={(val) => onPaginationChange(1, val)}
                  options={pagination.pageSizeOptions.map((size) => ({
                    label: `${size} dòng`,
                    value: Number(size),
                  }))}
                  size="middle"
                  variant="filled" // Cleaner look on mobile
                  className="w-[100px]"
                />
                <Pagination
                  simple
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={totalItems}
                  onChange={onPaginationChange}
                  size="small"
                />
              </div>
              <div className="text-center">
                <span className="text-[11px] text-gray-400 bg-gray-50 px-3 py-1 rounded-full">
                  Tổng <strong className="text-gray-600">{totalItems}</strong> bản ghi
                </span>
              </div>
            </div>
          ) : (
            // Desktop Layout
            <div className="flex flex-col-reverse sm:flex-row justify-between items-center gap-4">
              <div className="text-xs text-gray-500 font-medium text-center sm:text-left">
                Hiển thị{' '}
                <span className="text-blue-600 font-bold">{(pagination.current - 1) * pagination.pageSize + 1}</span> -{' '}
                <span className="text-blue-600 font-bold">
                  {Math.min(pagination.current * pagination.pageSize, totalItems)}
                </span>{' '}
                trên tổng số <span className="text-gray-900 font-bold">{totalItems}</span> kết quả
              </div>

              <div className="custom-pagination">
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={totalItems}
                  onChange={onPaginationChange}
                  showSizeChanger
                  pageSizeOptions={pagination.pageSizeOptions}
                  locale={{ items_per_page: '/ trang' }}
                  className="flex items-center gap-2"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

DataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  apiCallBack: PropTypes.func.isRequired,
  queryKey: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  propsInput: PropTypes.object,
  isRefetch: PropTypes.number,
  whereCondition: PropTypes.object,
  onRowClick: PropTypes.func,
};
